import React from 'react';
import { Smartphone, Download, Bell, Shield, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/user/Navbar';
import Footer from '../../../components/user/Footer';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const MobileApplications: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [benefitsRef, benefitsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 to-white">
        <div className={`max-w-7xl mx-auto transition-all duration-700 ${
          heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <Link 
            to="/services" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Services
          </Link>
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <Smartphone className="text-primary-600" size={64} />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-secondary-900 mb-4 tracking-tight">
                Mobile Applications
              </h1>
              <p className="text-xl text-secondary-600">
                Native mobile apps for iOS and Android with seamless experience
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-secondary-900 mb-6">
                Take Your Business Mobile
              </h2>
              <p className="text-lg text-secondary-600 leading-relaxed mb-6">
                Our native mobile applications provide a seamless experience for both customers 
                and business owners. Built for iOS and Android, our apps offer full functionality 
                with offline capabilities, push notifications, and biometric security.
              </p>
              <p className="text-lg text-secondary-600 leading-relaxed">
                Whether you're a customer making payments on the go or a business owner managing 
                your store, our mobile apps provide the convenience and functionality you need 
                wherever you are.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-12 text-white shadow-soft">
              <Download size={48} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Available Everywhere</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                Download our apps from the App Store or Google Play. Available for all iOS and 
                Android devices with regular updates and new features.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 px-4 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            featuresVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-secondary-600">
              Everything you need in a mobile app
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Smartphone size={40} />,
                title: 'iOS & Android Apps',
                description: 'Native apps for both iOS and Android platforms. Optimized performance and native user experience.'
              },
              {
                icon: <Download size={40} />,
                title: 'Offline Capabilities',
                description: 'Continue using key features even without internet connection. Sync data when connection is restored.'
              },
              {
                icon: <Bell size={40} />,
                title: 'Push Notifications',
                description: 'Get instant notifications for payments, offers, updates, and important alerts. Stay informed in real-time.'
              },
              {
                icon: <Shield size={40} />,
                title: 'Biometric Security',
                description: 'Secure your app with fingerprint or face recognition. Quick and secure access to your account.'
              },
              {
                icon: <Smartphone size={40} />,
                title: 'QR Code Scanner',
                description: 'Built-in QR code scanner for instant payments. Scan and pay in seconds with your mobile device.'
              },
              {
                icon: <CheckCircle size={40} />,
                title: 'Real-time Sync',
                description: 'All data syncs in real-time across devices. Access your account from any device seamlessly.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  featuresVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            benefitsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Why Use Our Mobile Apps?
            </h2>
            <p className="text-xl text-secondary-600">
              Benefits that enhance your experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Convenience',
                description: 'Access all features from your mobile device. Make payments, manage your business, and discover new places on the go.'
              },
              {
                title: 'Faster Performance',
                description: 'Native apps provide faster performance than web browsers. Optimized for mobile devices with smooth animations.'
              },
              {
                title: 'Better User Experience',
                description: 'Designed specifically for mobile with intuitive interfaces. Easy navigation and user-friendly design.'
              },
              {
                title: 'Offline Access',
                description: 'Continue using key features even without internet. View history, check balances, and more offline.'
              },
              {
                title: 'Instant Notifications',
                description: 'Get real-time push notifications for important updates. Never miss a payment or important alert.'
              },
              {
                title: 'Secure & Private',
                description: 'Biometric authentication and encrypted data storage. Your information is secure and private.'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 transition-all duration-500 ${
                  benefitsVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <CheckCircle className="text-primary-500 mr-3" size={24} />
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-primary-500">
        <div className={`max-w-4xl mx-auto text-center text-white transition-all duration-700 ${
          ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl font-bold mb-6 tracking-tight">
            Ready to Download?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Download our mobile apps and take Rita with you wherever you go
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/app"
              className="bg-white text-primary-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-secondary-50 transition-all duration-300 shadow-xl hover:scale-110 hover:-translate-y-1"
            >
              Download App
            </Link>
            <Link
              to="/services"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MobileApplications;

